import React, { useEffect, useState } from 'react';
import { Card, Statistic, Spin, Button, Modal, Tooltip } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { EmployeeFundResponse } from '../../../model/EmployeeFund.model';
import EmployeeFundService from '../../../service/employee-service/Employee-fund.service';
import AddFundsModal from '../AddFundsModal';
import EmployeeService from '../../../service/employee-service/Employee.service';
import AccountBalanceService from '../../../service/account-balance-service/Account-balance.service';
import { AccountBalance as AccountBalanceModel } from '../../../model/Account-balance.model';

// Đường dẫn API server (không dùng trực tiếp, đã có service dùng apiEndpoint)
const API_URL = process.env.REACT_APP_API_URL || 'http://mc.lht-jsc.com.vn:9090/api';

interface AccountBalanceProps {
  employeeId: number;
}

const AccountBalance: React.FC<AccountBalanceProps> = ({ employeeId }) => {
  const [fundInfo, setFundInfo] = useState<EmployeeFundResponse | null>(null);
  const [adminBalance, setAdminBalance] = useState<AccountBalanceModel | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  // Kiểm tra nếu nhân viên là admin
  const checkIfAdmin = async () => {
    try {
      const employee = await EmployeeService.getEmployeeById(employeeId);

      // Đơn giản hóa cách kiểm tra admin - dựa vào tên hoặc position
      const isAdminUser =
        (employee.name && employee.name.toLowerCase().includes('admin')) ||
        (employee.position &&
          employee.position.toLowerCase().includes('admin')) ||
        // Hoặc kiểm tra nếu id là 1 (thường Admin có ID đầu tiên)
        employee.id === 1;

      console.log('Checking if admin:', employee.name, isAdminUser);
      setIsAdmin(isAdminUser);
      return isAdminUser;
    } catch (error) {
      console.error('Error checking if employee is admin:', error);
      return false;
    }
  };

  // Lấy số dư tài khoản admin
  const fetchAdminAccountBalance = async () => {
    try {
      console.log('Fetching admin account balance...');
      // Sử dụng service có sẵn với đường dẫn đúng
      const data = await AccountBalanceService.getAccountBalance();
      console.log('Admin account balance response:', data);

      if (data) {
        setAdminBalance(data);
      } else {
        console.warn('Empty account balance data');
      }
    } catch (error) {
      console.error('Error fetching admin account balance:', error);
    }
  };

  const fetchFundInfo = async () => {
    try {
      const data = await EmployeeFundService.getFundByEmployeeId(employeeId);
      setFundInfo(data);
    } catch (error) {
      console.error('Error fetching fund info:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const adminCheck = await checkIfAdmin();

      if (adminCheck) {
        await fetchAdminAccountBalance();
        setLoading(false);
      } else {
        await fetchFundInfo();
      }
    };

    loadData();
  }, [employeeId]);

  if (loading) {
    return (
      <Card bordered={false}>
        <div className="flex justify-center items-center py-4">
          <Spin />
        </div>
      </Card>
    );
  }

  // Hiển thị thông tin số dư dựa vào loại tài khoản
  const renderBalanceContent = () => {
    if (isAdmin) {
      console.log('Rendering admin balance:', adminBalance);
      const balanceValue = adminBalance?.balanceAmount || 0;
      console.log('Admin balance value to display:', balanceValue);

      return (
        <Statistic
          title="Số dư tài khoản admin"
          value={balanceValue}
          suffix="VND"
        />
      );
    } else {
      const fundValue = fundInfo?.currentBalance || 0;
      console.log('Employee fund value to display:', fundValue);
      return (
        <Statistic title="Số dư hiện tại" value={fundValue} suffix="VND" />
      );
    }
  };

  const handleResetFund = () => {
    Modal.confirm({
      title: 'Xác nhận reset quỹ',
      content:
        'Bạn có chắc chắn muốn reset quỹ nhân viên về 0? Hành động này không thể hoàn tác.',
      okText: 'Xác nhận',
      cancelText: 'Hủy',
      okType: 'danger',
      onOk: async () => {
        try {
          await EmployeeFundService.resetFund(employeeId);
          fetchFundInfo();
          Modal.success({
            title: 'Thành công',
            content: 'Đã reset quỹ nhân viên về 0.',
          });
        } catch (error) {
          console.error('Error resetting fund:', error);
          Modal.error({
            title: 'Lỗi',
            content: 'Có lỗi xảy ra khi reset quỹ nhân viên.',
          });
        }
      },
    });
  };

  const handleResetAdminBalance = () => {
    Modal.confirm({
      title: 'Xác nhận reset số dư admin',
      content:
        'Bạn có chắc chắn muốn reset số dư tài khoản admin về 0? Hành động này không thể hoàn tác.',
      okText: 'Xác nhận',
      cancelText: 'Hủy',
      okType: 'danger',
      onOk: async () => {
        try {
          await AccountBalanceService.resetBalance();
          await fetchAdminAccountBalance();
          Modal.success({
            title: 'Thành công',
            content: 'Đã reset số dư tài khoản admin về 0.',
          });
        } catch (error) {
          console.error('Error resetting admin balance:', error);
          Modal.error({
            title: 'Lỗi',
            content: 'Có lỗi xảy ra khi reset số dư tài khoản admin.',
          });
        }
      },
    });
  };

  return (
    <Card
      title="Số dư tài khoản"
      bordered
      className="shadow-sm hover:shadow-md transition-shadow duration-300"
      extra={
        <div className="flex items-center gap-2">
          {!isAdmin && (
            <>
              <div
                className="flex items-center gap-2 cursor-pointer"
                onClick={() =>
                  document.getElementById('addFundsButton')?.click()
                }
              >
                <AddFundsModal
                  employeeId={employeeId}
                  onSuccess={fetchFundInfo}
                />
              </div>
              <Tooltip title="Reset quỹ nhân viên về 0">
                <Button
                  type="default"
                  danger
                  icon={<ReloadOutlined />}
                  onClick={handleResetFund}
                />
              </Tooltip>
            </>
          )}
          {isAdmin && (
            <Tooltip title="Reset số dư admin về 0">
              <Button
                type="default"
                danger
                icon={<ReloadOutlined />}
                onClick={handleResetAdminBalance}
              />
            </Tooltip>
          )}
        </div>
      }
    >
      {renderBalanceContent()}
    </Card>
  );
};

export default AccountBalance;
